import { AxiosError } from "axios";
import useNotificationHandler from "hooks/useNotificationHandler";
import { MUTATION_KEYS, QUERY_KEYS } from "library/shared";
import { IClient, IClientAndBenefitCategories } from "library/types";
import { useMutation, useQueryClient } from "react-query";
import api from "service/api";
import { useInvalidateBenefitCategories } from "./useGetBenefitCategories";
import { useInvalidateSingleClient } from "./useGetSingleClient";
import { useLocation } from "react-router-dom";
import { ROUTES } from "library/constants";

type Params = {
  countryCode: number | string;
  clientInternalNumberGOS: number | string;
  payload: IClient;
};

const updateClient = async ({
  countryCode,
  clientInternalNumberGOS,
  payload,
}: Params): Promise<IClient> => {
  const response = await api.patch(
    `/client/${countryCode}/${clientInternalNumberGOS}`,
    {
      ...payload,
    }
  );

  return response.data as IClient;
};

export function useUpdateClient(refetch = true) {
  const queryClient = useQueryClient();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();
  const invalidateBenefitCategories = useInvalidateBenefitCategories();
  const invalidateSingleClient = useInvalidateSingleClient();
  const location = useLocation();
  const isHrPage = location.pathname.includes(ROUTES.HR);
  return useMutation(updateClient, {
    mutationKey: MUTATION_KEYS.updateClient,
    onSuccess: (updatedData) => {
      if (refetch) {
        if (isHrPage) {
          invalidateSingleClient();
        } else {
          invalidateBenefitCategories();
        }
      } else {
        if (isHrPage) {
          queryClient.setQueryData(
            [
              QUERY_KEYS.singleClient,
              updatedData.countryCode?.toString(),
              updatedData.clientInternalNumberGos?.toString(),
            ],
            updatedData
          );
        } else {
          const prevBenefitCategories = queryClient.getQueryData([
            QUERY_KEYS.benefitCategories,
            updatedData.countryCode?.toString(),
            updatedData.clientInternalNumberGos?.toString(),
          ]) as IClientAndBenefitCategories;

          queryClient.setQueriesData(
            [
              QUERY_KEYS.benefitCategories,
              updatedData.countryCode?.toString(),
              updatedData.clientInternalNumberGos?.toString(),
            ],
            {
              ...prevBenefitCategories,
              client: updatedData,
            }
          );
        }
      }
    },
    onError: (error: AxiosError) => {
      handleAddAxiosErrorNotification(error);
    },
  });
}
